/** Draws optional wrapped caption text on top of a rendered background
 *  canvas — shared by the Background Maker's live preview and final export
 *  so what you see is exactly what gets downloaded/shared. Text is anchored
 *  at an arbitrary relative point (x, y in 0..1) rather than a fixed
 *  top/center/bottom slot, so it can be dragged anywhere on the canvas. */

export interface TextOverlayOptions {
  text: string;
  fontFamily: string;
  color: string;
  /** Relative anchor position, 0..1 across the canvas. */
  x: number;
  y: number;
  /** Relative size — canvas font size is derived from the shorter canvas edge. */
  scale: "sm" | "md" | "lg";
}

const SCALE_RATIO: Record<TextOverlayOptions["scale"], number> = {
  sm: 0.052,
  md: 0.072,
  lg: 0.095,
};

const EDGE_MARGIN = 0.04;

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split("\n")) {
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      lines.push("");
      continue;
    }
    let current = words[0];
    for (let i = 1; i < words.length; i++) {
      const candidate = `${current} ${words[i]}`;
      if (ctx.measureText(candidate).width <= maxWidth) {
        current = candidate;
      } else {
        lines.push(current);
        current = words[i];
      }
    }
    lines.push(current);
  }
  return lines;
}

/** Presets for the quick-pick 3x3 alignment grid — matches the margins used
 *  by the old fixed top/center/bottom positions. */
export const TEXT_POSITION_PRESETS: { id: string; x: number; y: number; label: string }[] = [
  { id: "tl", x: 0.15, y: 0.14, label: "Top left" },
  { id: "tc", x: 0.5, y: 0.14, label: "Top center" },
  { id: "tr", x: 0.85, y: 0.14, label: "Top right" },
  { id: "ml", x: 0.15, y: 0.5, label: "Middle left" },
  { id: "mc", x: 0.5, y: 0.5, label: "Center" },
  { id: "mr", x: 0.85, y: 0.5, label: "Middle right" },
  { id: "bl", x: 0.15, y: 0.86, label: "Bottom left" },
  { id: "bc", x: 0.5, y: 0.86, label: "Bottom center" },
  { id: "br", x: 0.85, y: 0.86, label: "Bottom right" },
];

export function drawTextOverlay(ctx: CanvasRenderingContext2D, w: number, h: number, opts: TextOverlayOptions) {
  const trimmed = opts.text.trim();
  if (!trimmed) return;

  const fontSize = Math.round(Math.min(w, h) * SCALE_RATIO[opts.scale]);
  const lineHeight = fontSize * 1.28;

  ctx.font = `700 ${fontSize}px ${opts.fontFamily}`;
  ctx.textBaseline = "middle";

  // Anchor x decides both where the block sits and how it's aligned: near
  // the left edge it left-aligns and grows rightward, near the right edge
  // it right-aligns and grows leftward, otherwise it centers — so text
  // dragged into a corner still reads naturally instead of overflowing.
  const anchorX = w * clamp01(opts.x);
  const anchorY = h * clamp01(opts.y);
  let align: CanvasTextAlign;
  let maxWidth: number;
  if (opts.x < 0.35) {
    align = "left";
    maxWidth = w - anchorX - w * EDGE_MARGIN;
  } else if (opts.x > 0.65) {
    align = "right";
    maxWidth = anchorX - w * EDGE_MARGIN;
  } else {
    align = "center";
    maxWidth = Math.min(anchorX, w - anchorX) * 2 - w * EDGE_MARGIN;
  }
  maxWidth = Math.max(maxWidth, w * 0.2);
  ctx.textAlign = align;

  const lines = wrapLines(ctx, trimmed, maxWidth).slice(0, 8);
  const blockHeight = lines.length * lineHeight;

  const minY = blockHeight / 2 + h * EDGE_MARGIN - lineHeight / 2;
  const maxY = h - blockHeight / 2 - h * EDGE_MARGIN + lineHeight / 2;
  const startY = Math.min(Math.max(anchorY - blockHeight / 2 + lineHeight / 2, minY), Math.max(minY, maxY));

  ctx.shadowColor = "rgba(0,0,0,0.35)";
  ctx.shadowBlur = fontSize * 0.25;
  ctx.shadowOffsetY = fontSize * 0.03;

  lines.forEach((line, i) => {
    ctx.fillStyle = opts.color;
    ctx.fillText(line, anchorX, startY + i * lineHeight);
  });

  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}
